
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import type { SingleStepCompetitionContext } from '@/helpers/sports/config';
import { contextSportIs, getSportPageContext } from '@/helpers/sports/config';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import {
	GetCompetitionPageV2Document,
	GetCompetitionPageV2QueryVariables,
} from '@/pages/graphql/queries/GetCompetitionPageV2.query';

import {
	ObjectType,
	Platform,
	SportCompetitionV2,
	SportEventAggregationKeyV2,
	SportEventStatus,
	SportType,
	SraPlacement,
} from '@/@types/graphql-types';
import AdUnit from '@/components/AdUnit.vue';
import { CollectionType } from '@/enums/collection-type';

import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import {
	getCompetitionSportSpinningText,
	SportCompetitionSpinningTextOptions,
} from '@/components/sports/spinning-texts';
import { isMultiStepSport, SportLocale } from '@/enums/sports';
import { LocaleResponse } from '@/helpers/locale-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import type { UrlMetadataResponse } from '@/interfaces/responses/url-metadata';
import { BreadcrumbItem, SportPageType } from '@/interfaces/sport-graphql';
import { CompetitionStages } from '@/interfaces/sport/competition-stages';
import type { FilterCollection } from '@/stores/filter.store';

import { competitionPageTexts } from '@/pages/sports/competition-page-texts-config';

import ProvideUpcomingSportEvents from '@/components/sports/single-step/ProvideUpcomingSportEvents.vue';

import Breadcrumbs from '@/components/sports/common/Breadcrumbs.vue';
import CompetitionFixtures from '@/components/sports/common/competition/fixtures/CompetitionFixtures.vue';
import CompetitionLogo from '@/components/sports/common/competition/logos/CompetitionLogo.vue';
import SingleStepEventEncounterList from '@/components/sports/common/SingleStepEventEncounterList.vue';
import Header from '@/components/sports/common/Header.vue';
import ProviderStreamsComparator from '@/components/sports/common/provider-streams-comparator/ProviderStreamsComparator.vue';
import SportsLayout from '@/components/sports/common/SportsLayout.vue';
import SportSpinningText from '@/components/sports/single-step/SportSpinningText.vue';
const StreamLanguageSwitcher = () => import('@/components/sports/common/StreamLanguageSwitcher.vue');

const Filter = namespace('filter');
const Language = namespace('language');
const Meta = namespace('meta');
const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'SingleStepCompetitionPage',
	components: {
		ProvideUpcomingSportEvents,
		Breadcrumbs,
		CompetitionFixtures,
		SingleStepEventEncounterList,
		Header,
		ProviderStreamsComparator,
		SportsLayout,
		StreamLanguageSwitcher,
		SportSpinningText,
		CompetitionLogo,
		AdUnit,
	},
	apollo: {
		competitionData: {
			query: GetCompetitionPageV2Document,
			variables(): GetCompetitionPageV2QueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.urlV2.node;
			},
			result(_result, _key) {
				this.SET_JSON_LD(this.schemaData);
			},
			skip() {
				if (this.$route.name != null && this.$route.name.includes('competition')) return false;
				return true;
			},
		},
	},
})
export default class SingleStepCompetitionPage extends Vue {
	competitionData: SportCompetitionV2;
	isLoadingMore = false;

	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.State webLocale: SportLocale;
	@Language.Getter localeObject: LocaleResponse;

	@Filter.Getter(CollectionType.SPORTS) filtersSport: FilterCollection;

	@Routing.State urlMetadata: UrlMetadataResponse;

	@Meta.Mutation SET_JSON_LD: (content: string) => void;

	@User.Getter isLoggedIn: () => boolean;

	context = {} as SingleStepCompetitionContext;

	@Watch('$route.meta.sport', { immediate: true })
	setContext(newMeta: SportType | undefined) {
		if (newMeta && newMeta !== this.context.sportType && !isMultiStepSport(newMeta)) {
			this.context = getSportPageContext({
				sport: this.$route.meta!.sport as SportType,
				pageType: SportPageType.SportCompetition,
			});
		}
	}

	get SraPlacement() {
		return SraPlacement;
	}

	get queryFrom() {
		return shiftDateWithTimezone(this.localeObject.timezone);
	}

	get queryVariables(): GetCompetitionPageV2QueryVariables {
		return {
			language: this.language,
			country: this.country,
			fullPath: this.$route.path,
			platform: Platform.Web,
			first: 20,
			filter: {
				startAt: {
					from: this.queryFrom.toISOString(),
				},
				packages: this.filtersSport.providers ?? [],
				sports: [this.context.sportType],
			},
			timezoneOffset: 0,
		};
	}

	get isDataLoading() {
		return this.$apollo.queries.competitionData?.loading;
	}

	get roundCount() {
		return this.competitionData?.sportEventsV2.edges?.length ?? 0;
	}

	get breadcrumbs(): BreadcrumbItem[] {
		// @todo remove when there are no duplicate event items that cause `get breadcrumbs` to stumble and break the whole page
		if (this.competitionData?.content == null) return [];

		return [
			// e.g. Sport
			{ name: this.$tc('WEBAPP_SPORTS'), routeName: 'app.sports.all' },

			// e.g. Fussball
			{
				name: this.$tc(this.context.sportName),
				objectType: SportPageType.SportOverview,
				url: this.context.getOverviewUrl(this.webLocale),
			},

			// e.g. Bundesliga
			{
				name: this.competitionData.content.name,
				objectType: ObjectType.SportCompetition,
				objectId: this.competitionData.objectId,
				url: this.competitionData.content.fullPath,
			},
		];
	}

	getSpinningText(options: SportCompetitionSpinningTextOptions) {
		return getCompetitionSportSpinningText(options);
	}

	get schemaData() {
		const schemaDataObject = {
			'@context': 'http://schema.org',
			'@type': 'EventSeries',
			name: this.competitionData.content.name,
			subEvents: this.schemaEvents,
		};
		return JSON.parse(JSON.stringify(schemaDataObject).replace(/&quot;/g, '"')) || '';
	}

	get events() {
		return this.competitionData?.sportEventsV2?.edges;
	}

	get schemaEvents() {
		const events = this.competitionData?.sportEventsV2?.edges || [];

		return events.map((event: any) => {
			const eventData = event.node;

			const { key, params } = this.context.getEventTitle(eventData, this.webLocale);
			const eventTitle = this.$t(key, params);

			const prices =
				eventData?.offers?.map((offer: any) => offer.retailPrice as number).filter((v: any) => v != null) ?? [];

			let eventStatus = '';
			switch (eventData.content?.status) {
				case SportEventStatus.Postponed:
					eventStatus = 'EventPostponed';
					break;
				case SportEventStatus.Cancelled:
					eventStatus = 'EventCancelled';
					break;
				default:
					eventStatus = 'EventScheduled';
			}

			return {
				'@context': 'http://schema.org',
				'@type': 'BroadcastEvent',
				name:
					`${eventTitle} ${this.competitionData.content.name} ${
						eventData.competition?.activeSeason?.name ?? ''
					}` ?? '',
				isLiveBroadcast: (eventData?.offers ?? []).length > 0,
				startDate: eventData.content?.startAt ?? '',
				endDate: eventData.content?.endAt ?? '',
				broadcastOfEvent: {
					'@type': 'SportsEvent',
					sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
					eventStatus: `https://schema.org/${eventStatus}`,
					name:
						`${eventTitle} ${this.competitionData.content.name} ${
							eventData.competition?.activeSeason?.name ?? ''
						}` ?? '',
					awayTeam: {
						'@type': 'SportsTeam',
						name:
							eventData?.competitors?.awayTeam?.competitors
								.map((c: any) => c.content.name)
								.find(Boolean) ?? '',
						sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
					},
					homeTeam: {
						'@type': 'SportsTeam',
						name:
							eventData?.competitors?.homeTeam?.competitors
								.map((c: any) => c.content.name)
								.find(Boolean) ?? '',
						sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
					},
					startDate: eventData.content?.startAt ?? '',
					endDate: eventData.content?.endAt ?? '',
					location: {
						'@type': 'Place',
						name: eventData.content?.venue?.name,
					},
					offers: eventData?.offers
						? {
								'@type': 'AggregateOffer',
								highPrice: Math.max(...prices) ?? undefined,
								lowPrice: Math.min(...prices) ?? undefined,
								priceCurrency: eventData?.offers.map((offer: any) => offer.currency).find(Boolean),
								offerCount: eventData?.offers.length,
								offers: eventData?.offers.map((offer: any) => ({
									'@type': 'Offer',
									name: `${eventTitle} ${offer.presentationType} ${offer.monetizationType}`,
									price: offer.retailPrice,
									priceCurrency: offer.currency,
									url: offer.standardWebURL,
								})),
						  }
						: null,
					audience: {
						'@type': 'Audience',
						name: 'Sports Fans',
					},
				},
			};
		});
	}

	get shouldRenderStreamLanguageSwitcher() {
		return contextSportIs.esports(this.context) && this.context.meta.showStreamSwitcher;
	}

	get translatedCountry() {
		if (this.country === 'US') return this.$t('WEBAPP_COUNTRY_THE_US');
		if (this.country === 'GB') return this.$t('WEBAPP_COUNTRY_THE_GB');

		return this.$t('WEBAPP_COUNTRY_' + this.country);
	}

	get availableStreamLanguages() {
		if (!this.shouldRenderStreamLanguageSwitcher) return [];

		const languages = (this.competitionData.sportEventsV2.edges ?? [])
			.flatMap(event => event.node.offers ?? [])
			.map(offer => offer.audioLanguage);

		return [...new Set(languages)];
	}

	get competitionText() {
		// @ts-ignore
		return competitionPageTexts?.[this.competitionData.id]?.[this.country] ?? '';
	}

	// TRACKING
	activated() {
		// note: all sport pages are excluded from automatic page tracking
		/**
		 * setTimeout added here to prevent pageView to be triggered before link click track event.
		 * that's because when adding the @click.native to router-link the track event will get triggered
		 * after the navigation which causes the pageView to get triggered first.
		 *
		 */
		setTimeout(() => {
			TrackingHelper.trackPage(this.$route.path, this.trackingContexts);
		});
	}

	get trackingContexts(): SnowplowSportContext[] {
		const sportContext = new SnowplowSportContext(
			this.$route.meta!.sport as SportType,
			SportPageType.SportCompetition
		);
		sportContext.competitionId = this.competitionData?.objectId;
		sportContext.seasonId = parseInt(this.competitionData?.activeSeason?.name || '');

		return sportContext !== null ? [sportContext] : [];
	}

	get competitionStages(): CompetitionStages[] {
		const bucket =
			this.competitionData?.sportEventsV2?.edges?.reduce((acc: Record<string, CompetitionStages>, edge) => {
				let key = '';
				const { name, number } = edge?.node?.content?.competitionStage;
				key = name + number;

				const bucket = acc[key] ?? { competitionStage: edge?.node?.content?.competitionStage, data: [] };
				bucket.data.push(edge);
				acc[key] = bucket;

				return acc;
			}, {}) || {};
		return Object.values(bucket);
	}

	async onLoadNext() {
		const pageInfo = this.competitionData.sportEventsV2.pageInfo;
		this.isLoadingMore = true;
		this.$apollo.queries.competitionData
			.fetchMore({
				variables: { after: pageInfo.endCursor },
				updateQuery(previousResult, { fetchMoreResult }) {
					previousResult.urlV2.node.sportEventsV2.pageInfo =
						fetchMoreResult.urlV2.node.sportEventsV2.pageInfo;

					previousResult.urlV2.node.sportEventsV2.edges = [
						...previousResult.urlV2.node.sportEventsV2.edges,
						...fetchMoreResult.urlV2.node.sportEventsV2.edges,
					];

					return previousResult;
				},
			})
			.catch(e => {
				captureMessageForSentry(
					'[GraphQL Competition Page Load More Buckets Issue]:',
					{ error: e, where: 'Component: CompetitionFixtures, method: onLoadNext' },
					'error'
				);
			})
			.finally(() => {
				this.isLoadingMore = false;
			});
	}

	get isNBA(): boolean {
		return this.competitionData?.id === 'scn-bkb-8331232';
	}

	get isNFL(): boolean {
		return this.competitionData?.id === 'scn-afb-7440571';
	}

	get isMLB(): boolean {
		return this.competitionData?.id === 'scn-bsb-11678542';
	}

	get isNHL(): boolean {
		return this.competitionData?.id === 'scn-ice-17698463';
	}

	getStage(key: SportEventAggregationKeyV2) {
		const now = new Date().getTime();

		if (this.isNBA) {
			if (now <= new Date('2024-04-14').setUTCHours(23, 59, 59, 999))
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
			return this.$t('SPORTS_WEBAPP_GAMES');
		}

		if (this.isNFL) {
			if (
				now <= new Date('2024-01-12').setUTCHours(23, 59, 59, 999) &&
				![
					'AMERICAN_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
					'AMERICAN_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
					'AMERICAN_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
					'NATIONAL_FOOTBALL_CONFERENCE_CHAMPIONSHIP',
					'NATIONAL_FOOTBALL_CONFERENCE_DIVISIONAL_ROUND',
					'NATIONAL_FOOTBALL_CONFERENCE_WILD_CARD_ROUND',
				].includes(key.competitionStage?.name as string)
			)
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
		}

		if (this.isMLB) {
			if (now <= new Date('2023-10-01').setUTCHours(23, 59, 59, 999))
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
		}

		if (this.isNHL) {
			if (now <= new Date('2024-04-18').setUTCHours(23, 59, 59, 999))
				return this.$tc('SPORTS_WEBAPP_REGULAR_SEASON');
		}

		return this.context.getStageHeading(key.competitionStage!, this.$t.bind(this));
	}

	getStageFromEvent(event: CompetitionStages) {
		return this.getStage({ competitionStage: event.competitionStage });
	}
}
